import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

/**
 * Notifie l'utilisateur qu'une nouvelle version de l'app est déployée et l'invite
 * à recharger — pour éviter qu'il reste sur un bundle périmé en cache.
 *
 * Fonctionnement : la version du bundle EN COURS est bakée au build
 * (`__APP_VERSION__`, injecté depuis public/version.json par vite.config.ts).
 * On interroge périodiquement /version.json (servi tel quel, non hashé) ; dès
 * que la version servie diffère de celle bakée, un toast persistant propose de
 * recharger.
 *
 * Déploiement : incrémenter "version" dans public/version.json avant chaque
 * build → les clients déjà chargés détectent l'écart et se voient proposer le
 * rechargement.
 */
const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 min

export default function UpdateNotifier() {
  const notified = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      // Une seule notification, et pas de requête si l'onglet est en arrière-plan.
      if (notified.current || document.hidden) return;
      try {
        const res = await fetch(`/version.json?_=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (cancelled) return;
        if (
          data?.version !== undefined &&
          data.version !== __APP_VERSION__
        ) {
          notified.current = true;
          toast('Une nouvelle version est disponible', {
            description: 'Rechargez la page pour bénéficier des dernières améliorations.',
            duration: Infinity,
            action: {
              label: 'Recharger',
              onClick: () => window.location.reload(),
            },
          });
        }
      } catch {
        // Réseau indisponible : on réessaiera au prochain tick, sans bruit.
      }
    };

    check();
    const intervalId = window.setInterval(check, CHECK_INTERVAL_MS);
    const onFocus = () => check();
    window.addEventListener('focus', onFocus);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  return null;
}
